/**
 * Pure helpers for manipulating filter trees on the frontend.
 *
 * Tree shape (matches backend):
 *   Node  = Group | Rule
 *   Group = { _id, type: "group", logic: "and"|"or", children: Node[] }
 *   Rule  = { _id, type: "rule", field: string, op: string, value: any }
 *
 * Every node also carries an `_id` (frontend-only, stripped on serialize) so
 * React can use it for keys and the editor can find nodes by id.
 */

let _idCounter = 1;
export function newId() {
	_idCounter += 1;
	return "n" + Date.now().toString(36) + "_" + _idCounter.toString(36);
}

export function newRule({ field = null, op = null, value = null } = {}) {
	return { _id: newId(), type: "rule", field, op, value };
}

export function newGroup({ logic = "and", children = [] } = {}) {
	return { _id: newId(), type: "group", logic, children };
}

export function emptyTree() {
	return newGroup({ logic: "and", children: [] });
}

/** Recursively assign `_id`s to a tree loaded from server (which won't have them). */
export function assignIds(node) {
	if (!node || typeof node !== "object") return node;
	const next = { ...node, _id: node._id || newId() };
	if (next.type === "group" && Array.isArray(next.children)) {
		next.children = next.children.map(assignIds);
	}
	return next;
}

/** Strip frontend-only `_id` fields before serializing to send to the backend. */
export function stripIds(node) {
	if (!node || typeof node !== "object") return node;
	const { _id, ...rest } = node;
	if (rest.type === "group" && Array.isArray(rest.children)) {
		rest.children = rest.children.map(stripIds);
	}
	return rest;
}

/**
 * Immutable patch: returns a new tree with the node matching `targetId` replaced
 * by `patcher(node)`. If `patcher` returns null, the node is removed.
 */
export function updateNode(tree, targetId, patcher) {
	if (!tree) return tree;
	if (tree._id === targetId) {
		return patcher(tree);
	}
	if (tree.type === "group" && Array.isArray(tree.children)) {
		const newChildren = [];
		let changed = false;
		for (const child of tree.children) {
			const result = updateNode(child, targetId, patcher);
			if (result !== child) changed = true;
			if (result != null) newChildren.push(result);
		}
		if (changed) return { ...tree, children: newChildren };
	}
	return tree;
}

/** Append a child to the group with `parentId`. */
export function appendChild(tree, parentId, child) {
	return updateNode(tree, parentId, group => {
		if (group.type !== "group") return group;
		return { ...group, children: [...group.children, child] };
	});
}

/** Count total rules in a tree (any depth). */
export function countRules(node) {
	if (!node || typeof node !== "object") return 0;
	if (node.type === "rule") return 1;
	if (node.type === "group" && Array.isArray(node.children)) {
		return node.children.reduce((acc, c) => acc + countRules(c), 0);
	}
	return 0;
}

/** Returns true if the tree contains zero rules (regardless of how many empty groups). */
export function isEmpty(tree) {
	return countRules(tree) === 0;
}

/**
 * Is this rule complete enough to send to the server?
 *
 * A rule is incomplete when the user has picked a field/op but hasn't filled
 * in the value yet (e.g., "Status / Equals / Choose…"). Sending such rules
 * makes the backend reject the WHOLE filter request — so we silently skip
 * them on the way out.
 *
 * Ops whose `input` is "none" (is_empty, today, this_week, …) are always
 * complete since they don't need a value.
 */
export function isRuleComplete(rule, schema) {
	if (!rule || rule.type !== "rule") return false;
	if (!rule.field || !rule.op) return false;
	if (!schema || !schema.fields) return true;
	const fieldDef = schema.fields[rule.field];
	if (!fieldDef) return false;

	// Lazily required so utils don't pull React/component code.
	// eslint-disable-next-line global-require
	const { operatorDef } = require("./operatorDefinitions");
	const def = operatorDef(fieldDef.type, rule.op);
	if (!def) return false;

	const v = rule.value;
	switch (def.input) {
		case "none":
			return true;
		case "boolean":
			return v === true || v === false || v === "true" || v === "false" || v === 1 || v === 0;
		case "picklist_multi":
			return Array.isArray(v) && v.length > 0;
		case "date_range":
		case "number_range":
			return Array.isArray(v) && v[0] != null && v[0] !== "" && v[1] != null && v[1] !== "";
		default:
			return v != null && v !== "";
	}
}

/**
 * Returns a copy of the tree with incomplete rules and now-empty groups removed.
 * Pure — does not mutate the input. Used right before serializing to the server.
 */
export function pruneIncompleteRules(tree, schema) {
	if (!tree || typeof tree !== "object") return tree;
	if (tree.type === "rule") return isRuleComplete(tree, schema) ? tree : null;
	if (tree.type !== "group") return tree;

	const kept = [];
	for (const child of tree.children || []) {
		const pruned = pruneIncompleteRules(child, schema);
		if (pruned == null) continue;
		if (pruned.type === "group" && (pruned.children || []).length === 0) continue;
		kept.push(pruned);
	}
	return { ...tree, children: kept };
}
